import ky from 'ky';
import { z } from 'zod';
import { ENV } from 'src/utils/env';

export const TASK_STATUS_MAP = {
  QUEUED: 'QUEUED',
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  ERROR: 'ERROR',
  CANCELLED: 'CANCELLED',
  COMPLETE: 'COMPLETE',
} as const;

export const TASK_ACTION_MAP = {
  video: 'video',
  timeshift: 'timeshift',
  move: 'move',
  copy: 'copy',
  reingest: 'reingest',
  delete: 'delete',
  delete_and_reingest: 'deleteandreingest',
  pop_upload: 'pop_upload',
  dump: 'dump',
} as const;

export type TaskTypes = (typeof TASK_ACTION_MAP)[keyof typeof TASK_ACTION_MAP];

export type TaskStatus = (typeof TASK_STATUS_MAP)[keyof typeof TASK_STATUS_MAP];

export const TASK_SCHEMA = z.object({
  id: z.string(),
  status: z.enum(['PENDING', 'RUNNING', 'ERROR', 'CANCELLED', 'COMPLETE']),
  message: z.string(),
  author: z.string(),
  action_type: z.enum([
    'video',
    'timeshift',
    'move',
    'copy',
    'reingest',
    'delete',
    'deleteandreingest',
    'pop_upload',
    'dump',
    'reingest_preview',
  ]),
  created_time: z.string(),
  last_modified: z.string(),
  config: z
    .object({
      index1: z.string().or(z.number()),
      index2: z.string().or(z.number()).optional().nullable(),
      earliest1: z.number().optional().nullable(),
      latest1: z.number().optional().nullable(),
    })
    .catchall(z.string().or(z.number()).or(z.boolean()).nullable()),
});

export type Task = z.infer<typeof TASK_SCHEMA>;

export type ListTasksSchema = z.infer<typeof TASK_SCHEMA>[];

export type ListTasksPromiseParams = {
  signal: AbortSignal;
  filterParams: {
    task_status?: TaskStatus;
    task_type?: TaskTypes;
    search?: string;
    view_all_users?: boolean;
    pageNumber?: number;
    itemsPerPage?: number;
  };
};

const PAGINATED_LIST_TASKS_SCHEMA = z.object({
  tasks: z.array(TASK_SCHEMA),
  totalPages: z.number(),
  currentPage: z.number(),
  pageSize: z.number(),
});

export const listTasksPromise = async ({ signal, filterParams }: ListTasksPromiseParams) => {
  const res = await ky
    .post(`${ENV.FRONTEND_API_URL}/list_tasks`, {
      signal,
      body: JSON.stringify(filterParams),
    })
    .json();

  const result = PAGINATED_LIST_TASKS_SCHEMA.safeParse(res);

  if (!result.success) {
    console.error(result.error);
    return {
      tasks: [],
      totalPages: 0,
      currentPage: 0,
      pageSize: 0,
    };
  }

  return result.data;
};

export type ListTasks = Awaited<ReturnType<typeof listTasksPromise>>;
